import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CaretLeft, CaretUp, CaretDown, MagnifyingGlass, MapPin, Path, FilePdf, FileXls, WhatsappLogo, LinkSimple, Trash, Warning, CheckCircle, ArrowCounterClockwise, Compass, DeviceMobile, ArrowsClockwise, PencilSimple, Star } from '@phosphor-icons/react';
import API from '@/lib/api';
import { isIOS as isIOSPlatform } from '@/lib/platform';
import RouteKaart from '@/components/RouteKaart';
import FavoRondes from '@/components/FavoRondes';
import KlantBijzetten from '@/components/KlantBijzetten';

const PRIO = {
  paars: { cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  rood: { cls: 'bg-red-50 text-red-800 border-red-300' },
  oranje: { cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  groen: { cls: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  grijs: { cls: 'bg-gray-100 text-gray-700 border-gray-300' },
};
const vandaagISO = () => new Date().toISOString().slice(0, 10);
const fmtDatum = (iso) => { try { return new Date(`${iso}T12:00:00`).toLocaleDateString('nl-BE', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return iso; } };
const fmtDD = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : 'nooit');
const fmtEur = (v) => `€ ${Number(v || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDuur = (s) => { const m = Math.round((s || 0) / 60); return `${Math.floor(m / 60)}u ${String(m % 60).padStart(2, '0')}`; };
const fmtStamp = (iso) => { try { return new Date(iso).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const openUrl = (url) => { const a = document.createElement('a'); a.href = url; a.target = '_blank'; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove(); };
const bestandsnaam = (resp, fallback) => { const m = /filename="?([^";]+)"?/i.exec(resp?.headers?.['content-disposition'] || ''); return m ? decodeURIComponent(m[1]) : fallback; };
const downloadBlob = (resp, fallback) => {
  const fname = bestandsnaam(resp, fallback);
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement('a'); a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return fname;
};
const veilig = (s) => (s || 'route').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'route';
// Gsm/tablet: popups moeten synchroon geopend worden, downloads werken er niet betrouwbaar
const isMobiel = () => isIOSPlatform() || (typeof window !== 'undefined' && (window.innerWidth < 900 || (navigator.maxTouchPoints || 0) > 1));

const PrioPill = ({ p }) => p?.label ? <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold border ${(PRIO[p.kleur] || PRIO.grijs).cls}`}>{p.label}</span> : null;

const KandidaatRij = ({ k, checked, onToggle, onVerwijder }) => (
  <label className={`flex items-start gap-3 p-3 border-b border-gray-100 cursor-pointer ${checked ? 'bg-[#F5F8FF]' : 'bg-white'}`} data-testid={`vr-kandidaat-${k.klant_id}`}>
    <input type="checkbox" className="mt-1 w-5 h-5 shrink-0" checked={checked} onChange={onToggle} data-testid={`vr-kandidaat-check-${k.klant_id}`} />
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold text-sm text-gray-900 truncate">{k.klant_naam}</span>
        {k.geo_waarschuwing
          ? <span className="text-amber-600 shrink-0" title={k.geo_waarschuwing} data-testid={`vr-geo-waarschuwing-${k.klant_id}`}><Warning size={14} weight="fill" /></span>
          : <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${k.geo ? 'bg-emerald-500' : 'bg-gray-300'}`} title={k.geo ? 'Exact op de kaart gevonden' : 'Nog geen coördinaten'} />}
      </div>
      <div className="text-xs text-gray-600 truncate">{k.adres?.tekst}</div>
      {k.geo_waarschuwing && <div className="text-[11px] text-amber-700 mt-0.5">⚠ {k.geo_waarschuwing}</div>}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-gray-500">
        <PrioPill p={k.prioriteit} />
        <span>Laatste bezoek: <b>{fmtDD(k.laatste_bezoek)}</b></span>
        {k.openstaand > 0 && <span className="text-red-600 font-bold">Openstaand {fmtEur(k.openstaand)}</span>}
        {k.verkoper_naam && <span>👤 {k.verkoper_naam}</span>}
      </div>
    </div>
    {onVerwijder && (
      <button type="button" onClick={(e) => { e.preventDefault(); onVerwijder(); }} className="h-9 w-9 shrink-0 inline-flex items-center justify-center text-red-500 border border-red-200 bg-white" title="Uit de gekozen lijst halen" data-testid={`vr-kandidaat-weg-${k.klant_id}`}><Trash size={14} /></button>
    )}
  </label>
);

const StopRij = ({ s, kleur, idx, totaal, onOmhoog, onOmlaag, onVerwijder }) => (
  <div className="flex items-start gap-2 p-2.5 border-b border-gray-100 bg-white" data-testid={`vr-stop-${s.klant_id}`}>
    <div className="w-7 h-7 rounded-full text-white text-xs font-extrabold flex items-center justify-center shrink-0" style={{ background: kleur }}>{s.positie}</div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold text-sm truncate">{s.klant_naam}{s.geo_waarschuwing && <span className="text-amber-600 ml-1" title={s.geo_waarschuwing}>⚠</span>}</span>
        <span className="font-mono text-xs font-bold shrink-0">{s.eta}</span>
      </div>
      <div className="text-xs text-gray-600 truncate">{s.adres?.tekst}</div>
      <div className="flex flex-wrap gap-x-3 text-[11px] text-gray-500 mt-0.5">
        <span>+{Math.round((s.duur_s || 0) / 60)} min · {((s.afstand_m || 0) / 1000).toFixed(1)} km</span>
        <PrioPill p={s.prioriteit} />
        {s.openstaand > 0 && <span className="text-red-600 font-bold">{fmtEur(s.openstaand)}</span>}
        {s.status && s.status !== 'open' && <span className={`font-bold ${s.status === 'bezocht' ? 'text-emerald-700' : 'text-gray-500'}`}>{s.status === 'bezocht' ? '✓ bezocht' : '↷ overgeslagen'}</span>}
      </div>
    </div>
    {onOmhoog && (
      <div className="flex flex-col shrink-0">
        <button type="button" disabled={idx === 0} onClick={onOmhoog} className="h-7 w-8 inline-flex items-center justify-center text-gray-500 border border-gray-200 disabled:opacity-25" title="Vroeger bezoeken" data-testid={`vr-stop-omhoog-${s.klant_id}`}><CaretUp size={14} weight="bold" /></button>
        <button type="button" disabled={idx === totaal - 1} onClick={onOmlaag} className="h-7 w-8 inline-flex items-center justify-center text-gray-500 border border-gray-200 border-t-0 disabled:opacity-25" title="Later bezoeken" data-testid={`vr-stop-omlaag-${s.klant_id}`}><CaretDown size={14} weight="bold" /></button>
      </div>
    )}
    {onVerwijder && (
      <button type="button" onClick={onVerwijder} className="h-7 w-8 shrink-0 inline-flex items-center justify-center text-red-500 border border-red-200" title="Deze klant uit de route halen" data-testid={`vr-stop-weg-${s.klant_id}`}><Trash size={13} /></button>
    )}
  </div>
);

const RitKaart = ({ rit, nieuwste, onPdf, onExcel, onPdfAlle, onExcelAlle, onWhatsApp, onLink, onOpen, onVerwijder, onBewerk }) => {
  const n = rit.stops?.length || 0;
  const klaar = (rit.bezocht || 0) + (rit.overgeslagen || 0);
  const meerdere = (rit.dagen_totaal || 1) > 1;
  return (
    <div className={`bg-white border p-3 ${nieuwste ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-gray-200'}`} data-testid={`vr-rit-${rit.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-lg font-extrabold tabular-nums leading-none" style={{ color: '#002FA7' }} data-testid={`vr-rit-uur-${rit.id}`}>{rit.vertrek_uur} → {rit.eind_uur || '—'}</span>
            <span className="font-bold text-sm text-gray-900">{fmtDatum(rit.datum)}</span>
            {meerdere ? <span className="text-[11px] text-gray-400">dag {rit.dag_nr}/{rit.dagen_totaal}</span> : null}
          </div>
          <div className="text-xs text-gray-600 truncate mt-0.5">{rit.verkoper_naam || 'Geen verkoper'}{rit.titel ? ` · ${rit.titel}` : ''}</div>
          <div className="text-[11px] text-gray-500">{n} bezoeken · {((rit.totaal_afstand_m || 0) / 1000).toFixed(0)} km · rijden {fmtDuur(rit.rij_duur_s)}</div>
          {(rit.gewijzigd_at || rit.created_at) && (
            <div className="text-[10px] text-gray-400" data-testid={`vr-rit-stamp-${rit.id}`}>
              {rit.created_at ? `Opgemaakt ${fmtStamp(rit.created_at)}` : ''}{rit.gewijzigd_at ? ` · aangepast ${fmtStamp(rit.gewijzigd_at)}` : ''}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {nieuwste && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-emerald-600 text-white" data-testid={`vr-rit-nieuwste-${rit.id}`}>★ Laatst aangemaakt</span>}
          <span className={`text-[10px] font-bold px-1.5 py-0.5 ${rit.status === 'afgewerkt' ? 'bg-emerald-600 text-white' : rit.status === 'onderweg' ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-700'}`} data-testid={`vr-rit-status-${rit.id}`}>
            {rit.status === 'afgewerkt' ? 'Afgewerkt' : rit.status === 'onderweg' ? `Bezig ${klaar}/${n}` : 'Gepland'}
          </span>
        </div>
      </div>
      <div className="h-1.5 bg-gray-100 mt-2"><div className="h-full bg-emerald-500" style={{ width: `${n ? Math.round((klaar / n) * 100) : 0}%` }} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-2.5 text-xs sm:text-[11px] font-semibold">
        <button type="button" onClick={onBewerk} className="h-11 sm:h-9 border border-[#002FA7] text-[#002FA7] bg-white inline-flex items-center justify-center gap-1" data-testid={`vr-rit-bewerk-${rit.id}`} title="Volgorde aanpassen, stops verwijderen, datum of verkoper wijzigen"><PencilSimple size={15} weight="bold" /> Bewerken</button>
        <button type="button" onClick={onPdf} className="h-11 sm:h-9 border border-gray-300 bg-white inline-flex items-center justify-center gap-1" data-testid={`vr-rit-pdf-${rit.id}`} title="PDF openen en afdrukken (werkt ook op gsm)"><FilePdf size={15} className="text-red-600" /> PDF openen</button>
        <button type="button" onClick={onExcel} className="h-11 sm:h-9 border border-gray-300 bg-white inline-flex items-center justify-center gap-1" data-testid={`vr-rit-excel-${rit.id}`} title="Excel downloaden"><FileXls size={15} className="text-emerald-700" /> Excel</button>
        <button type="button" onClick={onWhatsApp} className="h-11 sm:h-9 border border-gray-300 bg-white inline-flex items-center justify-center gap-1" data-testid={`vr-rit-wa-${rit.id}`}><WhatsappLogo size={15} weight="fill" className="text-[#25D366]" /> WhatsApp</button>
        <button type="button" onClick={onOpen} className="h-11 sm:h-9 border border-gray-300 bg-white inline-flex items-center justify-center gap-1" data-testid={`vr-rit-open-${rit.id}`}><DeviceMobile size={15} /> Checklist</button>
        <button type="button" onClick={onLink} className="h-11 sm:h-9 border border-gray-300 bg-white inline-flex items-center justify-center gap-1" data-testid={`vr-rit-link-${rit.id}`}><LinkSimple size={15} /> Link</button>
        <button type="button" onClick={onVerwijder} className="h-11 sm:h-9 border border-red-200 text-red-600 bg-white inline-flex items-center justify-center gap-1" data-testid={`vr-rit-del-${rit.id}`}><Trash size={15} /> Wis</button>
      </div>
      {meerdere && (
        <div className="flex flex-col sm:flex-row gap-1.5 mt-1.5 text-xs sm:text-[11px] font-semibold">
          <button type="button" onClick={onPdfAlle} className="h-11 sm:h-8 flex-1 border border-dashed border-gray-300 bg-white inline-flex items-center justify-center gap-1 text-gray-700" data-testid={`vr-rit-pdf-alle-${rit.id}`}><FilePdf size={14} className="text-red-600" /> PDF alle {rit.dagen_totaal} dagen</button>
          <button type="button" onClick={onExcelAlle} className="h-11 sm:h-8 flex-1 border border-dashed border-gray-300 bg-white inline-flex items-center justify-center gap-1 text-gray-700" data-testid={`vr-rit-excel-alle-${rit.id}`}><FileXls size={14} className="text-emerald-700" /> Excel alle dagen</button>
        </div>
      )}
    </div>
  );
};

export default function VerkoopRoutePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const voorafIds = useMemo(() => location.state?.klant_ids || [], [location.state]);
  const [pcVan, setPcVan] = useState('');
  const [pcTot, setPcTot] = useState('');
  const [verkoperId, setVerkoperId] = useState('');
  const [alleenOpvolging, setAlleenOpvolging] = useState(true);
  const [kand, setKand] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [geoBezig, setGeoBezig] = useState(false);
  const [geoFouten, setGeoFouten] = useState([]);
  const [datum, setDatum] = useState(vandaagISO());
  const [maxPerDag, setMaxPerDag] = useState(12);
  const [bezoektijd, setBezoektijd] = useState(20);
  const [roundtrip, setRoundtrip] = useState(true);
  const [optimaliseer, setOptimaliseer] = useState('tijd');
  const [plan, setPlan] = useState(null);
  const [dagIdx, setDagIdx] = useState(0);
  const [titel, setTitel] = useState('');
  const [bezig, setBezig] = useState('');
  const [ritten, setRitten] = useState([]);
  const [stap, setStap] = useState(1);
  const [kaartView, setKaartView] = useState(null);
  const [bewerk, setBewerk] = useState(null);
  const [modus, setModus] = useState('postcode');
  const [zoekTerm, setZoekTerm] = useState('');
  const [zoekRes, setZoekRes] = useState([]);
  const [favos, setFavos] = useState([]);
  const [sorteer, setSorteer] = useState('datum');
  const kleur = '#002FA7';

  const nieuwsteId = useMemo(() => (ritten.length ? ritten.reduce((a, b) => ((b.created_at || '') > (a.created_at || '') ? b : a)).id : ''), [ritten]);
  const rittenGesorteerd = useMemo(() => (sorteer === 'nieuw' ? [...ritten].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')) : ritten), [ritten, sorteer]);

  const laadFavos = useCallback(async () => {
    try { const r = await API.get('/verkoopritten/sjablonen'); setFavos(r.data || []); } catch { /* stil */ }
  }, []);
  useEffect(() => { laadFavos(); }, [laadFavos]);

  const laadRitten = useCallback(async () => {
    try { const r = await API.get('/verkoopritten'); setRitten(r.data || []); } catch { /* stil */ }
  }, []);
  useEffect(() => { laadRitten(); }, [laadRitten]);

  const zoek = useCallback(async (ids) => {
    setBezig('zoek');
    try {
      const params = ids?.length ? { ids: ids.join(',') } : { postcode_van: pcVan, postcode_tot: pcTot, verkoper_id: verkoperId, alleen_opvolging: alleenOpvolging };
      const r = await API.get('/verkoopritten/kandidaten', { params });
      setKand(r.data);
      setSel(new Set((r.data.klanten || []).map(k => k.klant_id)));
      setPlan(null);
      setGeoFouten([]);
      if (!r.data.klanten?.length) toast.info('Geen klanten gevonden voor deze selectie');
    } catch (e) { toast.error('Zoeken mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  }, [pcVan, pcTot, verkoperId, alleenOpvolging]);

  useEffect(() => { if (voorafIds.length) { setAlleenOpvolging(false); zoek(voorafIds); } }, [voorafIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Klanten zelf kiezen: vrij zoeken op naam/stad/postcode en één per één toevoegen
  const zoekKlanten = async () => {
    const t = zoekTerm.trim();
    if (t.length < 2) { toast.info('Typ minstens 2 letters (naam, bedrijf, stad of postcode)'); return; }
    setBezig('zoekklant');
    try {
      const r = await API.get('/verkoopritten/kandidaten', { params: { zoek: t, verkoper_id: verkoperId } });
      setZoekRes(r.data.klanten || []);
      setKand(k => (k ? { ...k, depot: r.data.depot, vertrek_uur: r.data.vertrek_uur, verkopers: r.data.verkopers } : { ...r.data, klanten: [] }));
      if (!r.data.klanten?.length) toast.info(`Geen klant gevonden voor “${t}”`);
    } catch (e) { toast.error('Zoeken mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const voegToe = (k) => {
    setKand(p => ((p?.klanten || []).some(x => x.klant_id === k.klant_id) ? p : { ...p, klanten: [...(p?.klanten || []), k] }));
    setSel(s => new Set([...s, k.klant_id]));
    setPlan(null);
    toast.success(`${k.klant_naam} toegevoegd aan de route`);
  };

  const haalWeg = (id) => {
    setKand(p => ({ ...p, klanten: (p?.klanten || []).filter(x => x.klant_id !== id) }));
    setSel(s => { const n = new Set(s); n.delete(id); return n; });
    setPlan(null);
  };

  const wisselModus = (m) => {
    setModus(m);
    setKand(null); setSel(new Set()); setZoekRes([]); setZoekTerm(''); setPlan(null); setGeoFouten([]);
  };

  // ─── Favoriete rondes: vaste klantenselectie om elke maand opnieuw te rijden ───
  const favoIds = useMemo(() => (plan ? (plan.dagen || []).flatMap(d => (d.stops || []).map(s => s.klant_id)) : [...sel]), [plan, sel]);

  const favoBewaar = async () => {
    if (!favoIds.length) { toast.error('Kies eerst klanten voor deze ronde'); return; }
    const naam = window.prompt('Naam voor deze favoriete ronde (bv. "Ronde Antwerpen – maandelijks"):', titel || '');
    if (!naam?.trim()) return;
    setBezig('favo-bewaar');
    try {
      await API.post('/verkoopritten/sjablonen', {
        naam: naam.trim(), klant_ids: favoIds, verkoper_id: verkoperId === '__none__' ? '' : verkoperId, titel,
        max_per_dag: Number(maxPerDag) || 12, bezoektijd_min: Number(bezoektijd) || 0, roundtrip, optimaliseer,
      });
      toast.success(`Favoriete ronde "${naam.trim()}" bewaard met ${favoIds.length} klanten`);
      await laadFavos();
    } catch (e) { toast.error('Bewaren mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const favoLaden = async (f) => {
    setModus('zelf');
    setZoekRes([]); setZoekTerm('');
    setMaxPerDag(f.max_per_dag || 12);
    setBezoektijd(f.bezoektijd_min ?? 20);
    setRoundtrip(f.roundtrip !== false);
    setOptimaliseer(f.optimaliseer === 'afstand' ? 'afstand' : 'tijd');
    setVerkoperId(f.verkoper_id || '');
    setTitel(f.titel || f.naam || '');
    setStap(1);
    await zoek(f.klant_ids || []);
    toast.success(`Ronde "${f.naam}" geladen — kies de datum en bereken de route`);
  };

  const favoRijden = async (f) => {
    if (!window.confirm(`Ronde "${f.naam}" (${f.aantal_klanten} klanten) plannen en opslaan op ${fmtDatum(datum)}?`)) return;
    setBezig(`favo-rijden-${f.id}`);
    try {
      const r = await API.post(`/verkoopritten/sjablonen/${f.id}/rijden`, { datum });
      toast.success(`"${f.naam}" ingepland: ${r.data.totaal_stops} bezoeken over ${r.data.dagen_totaal} dag(en)`);
      if (r.data.overgeslagen?.length) toast.warning(`Overgeslagen (adres niet gevonden): ${r.data.overgeslagen.join(', ')}`);
      setPlan(null); setBewerk(null); setStap(3);
      await Promise.all([laadRitten(), laadFavos()]);
    } catch (e) { toast.error('Inplannen mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const favoWis = async (f) => {
    if (!window.confirm(`Favoriete ronde "${f.naam}" verwijderen? De reeds geplande routes blijven bestaan.`)) return;
    try { await API.delete(`/verkoopritten/sjablonen/${f.id}`); toast.success('Favoriete ronde verwijderd'); await laadFavos(); }
    catch (e) { toast.error('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message)); }
  };

  const geselecteerd = useMemo(() => (kand?.klanten || []).filter(k => sel.has(k.klant_id)), [kand, sel]);
  const zonderGeo = geselecteerd.filter(k => !k.geo && !geoFouten.some(f => f.klant_id === k.klant_id));

  const geocodeer = async (force = false) => {
    let pending = force ? geselecteerd.map(k => k.klant_id) : zonderGeo.map(k => k.klant_id);
    if (!pending.length && kand?.depot?.geo && !force) return true;
    setGeoBezig(true);
    const fouten = force ? [] : [...geoFouten];
    let waarschuwingen = 0;
    try {
      let mislukt = 0;
      for (let i = 0; i < 120 && (pending.length || !kand?.depot?.geo); i++) {
        let r;
        try {
          r = await API.post('/verkoopritten/geocode', { klant_ids: pending, force });
          mislukt = 0;
        } catch (e) {
          // Adressendienst limiteert soms (429) → even wachten en verder, niet alles laten mislukken
          mislukt++;
          if (mislukt >= 4) {
            toast.error(`Coördinaten ophalen mislukt (${pending.length} adres(sen) nog te doen): ` + (e.response?.data?.detail || e.message));
            setGeoFouten(fouten);
            return false;
          }
          toast.info(`Adressendienst even bezig — opnieuw proberen (${pending.length} te gaan)`, { duration: 2000 });
          await new Promise(res => setTimeout(res, 2500));
          continue;
        }
        const geo = r.data.geo || {};
        const warn = r.data.waarschuwingen || {};
        waarschuwingen += Object.keys(warn).length;
        setKand(k => ({ ...k, klanten: k.klanten.map(x => geo[x.klant_id] ? { ...x, geo: geo[x.klant_id], geo_waarschuwing: warn[x.klant_id] || '' } : x), depot: { ...k.depot, geo: r.data.depot_geo || k.depot?.geo } }));
        (r.data.fouten || []).forEach(f => { if (!fouten.some(x => x.klant_id === f.klant_id)) fouten.push(f); });
        pending = pending.filter(id => !geo[id] && !(r.data.fouten || []).some(f => f.klant_id === id));
        if (r.data.depot_fout) { toast.error(r.data.depot_fout); setGeoFouten(fouten); return false; }
        if (!pending.length) break;
      }
      setGeoFouten(fouten);
      if (fouten.length) toast.warning(`${fouten.length} adres(sen) niet gevonden — die worden overgeslagen`);
      if (waarschuwingen) toast.warning(`${waarschuwingen} adres(sen) niet exact gevonden (⚠) — controleer die adressen, anders klopt de route niet`);
      else if (force) toast.success('Alle adressen opnieuw opgezocht');
      return true;
    } catch (e) { toast.error('Coördinaten ophalen mislukt: ' + (e.response?.data?.detail || e.message)); return false; }
    finally { setGeoBezig(false); }
  };

  const bereken = async () => {
    if (!geselecteerd.length) { toast.error('Selecteer minstens 1 klant'); return; }
    const ok = await geocodeer();
    if (!ok) return;
    setBezig('plan');
    try {
      const ids = geselecteerd.filter(k => !geoFouten.some(f => f.klant_id === k.klant_id)).map(k => k.klant_id);
      const r = await API.post('/verkoopritten/plan', { klant_ids: ids, datum, max_per_dag: maxPerDag, bezoektijd_min: bezoektijd, roundtrip, optimaliseer });
      setPlan(r.data);
      setDagIdx(0);
      setStap(2);
      toast.success(`Route berekend: ${r.data.totaal_stops} bezoeken over ${r.data.dagen_totaal} dag(en) — ${(r.data.totaal_afstand_m / 1000).toFixed(0)} km`);
    } catch (e) { toast.error('Route berekenen mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const zetDag = (d) => setPlan(p => {
    const dagen = p.dagen.map((dd, i) => (i === dagIdx ? d : dd));
    return { ...p, dagen, totaal_afstand_m: dagen.reduce((a, dd) => a + (dd.totaal_afstand_m || 0), 0), totaal_stops: dagen.reduce((a, dd) => a + (dd.stops?.length || 0), 0) };
  });

  const verschuif = async (van, naar) => {
    const d = plan?.dagen?.[dagIdx];
    if (!d || naar < 0 || naar >= d.stops.length) return;
    const ids = d.stops.map(s => s.klant_id);
    const [x] = ids.splice(van, 1);
    ids.splice(naar, 0, x);
    setBezig('herbereken');
    try {
      const r = await API.post('/verkoopritten/herbereken', { planning: plan, dag_nr: d.dag_nr, klant_ids: ids });
      zetDag(r.data);
    } catch (e) { toast.error('Herberekenen mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  // Klant bijzetten in de (bewaarde) route: komt automatisch op de slimste plek
  const bijzetZoek = async (t) => {
    // Bewust zonder verkoper-filter: u moet elke klant kunnen bijzetten, ook als die aan een andere verkoper hangt
    try {
      const r = await API.get('/verkoopritten/kandidaten', { params: { zoek: t } });
      const inRoute = new Set(((plan?.dagen || []).flatMap(d => d.stops || [])).map(s => s.klant_id));
      return (r.data.klanten || []).map(k => ({ ...k, in_route: inRoute.has(k.klant_id) }));
    } catch (e) { toast.error('Zoeken mislukt: ' + (e.response?.data?.detail || e.message)); return []; }
  };

  const bijzetKlant = async (k) => {
    const d = plan?.dagen?.[dagIdx];
    if (!d) return false;
    setBezig('herbereken');
    try {
      const r = await API.post('/verkoopritten/bijzetten', { planning: plan, dag_nr: d.dag_nr, klant_id: k.klant_id });
      zetDag(r.data);
      setSel(s => new Set([...s, k.klant_id]));
      const b = r.data.bijgezet || {};
      toast.success(`${b.klant_naam || k.klant_naam} bijgezet op plek ${b.positie} — uren en km herberekend`);
      if (b.geo_waarschuwing) toast.warning(`${b.klant_naam}: ${b.geo_waarschuwing}`);
      return true;
    } catch (e) { toast.error('Bijzetten mislukt: ' + (e.response?.data?.detail || e.message)); return false; }
    finally { setBezig(''); }
  };

  const optimaliseerDag = async () => {
    const d = plan?.dagen?.[dagIdx];
    if (!d || d.stops.length < 3) { toast.info('Minstens 3 bezoeken nodig om te optimaliseren'); return; }
    setBezig('herbereken');
    try {
      const r = await API.post('/verkoopritten/herbereken', { planning: plan, dag_nr: d.dag_nr, klant_ids: d.stops.map(s => s.klant_id), optimaliseer_volgorde: true, optimaliseer });
      zetDag(r.data);
      toast.success(`Volgorde opnieuw berekend — ${(r.data.totaal_afstand_m / 1000).toFixed(1)} km, einde ${r.data.eind_uur}`);
    } catch (e) { toast.error('Optimaliseren mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const verwijderStop = async (idx) => {
    const d = plan?.dagen?.[dagIdx];
    if (!d || d.stops.length <= 1) { toast.error('Een route moet minstens 1 bezoek hebben'); return; }
    const weg = d.stops[idx];
    if (!window.confirm(`${weg.klant_naam} uit deze route halen?`)) return;
    const ids = d.stops.filter((_, i) => i !== idx).map(s => s.klant_id);
    setBezig('herbereken');
    try {
      const r = await API.post('/verkoopritten/herbereken', { planning: plan, dag_nr: d.dag_nr, klant_ids: ids });
      zetDag(r.data);
      setSel(s => { const n = new Set(s); n.delete(weg.klant_id); return n; });
      toast.success(`${weg.klant_naam} verwijderd — tijden herberekend`);
    } catch (e) { toast.error('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const bewerkStart = async (rit) => {
    setBezig('bewerk');
    try {
      const R = (await API.get(`/verkoopritten/${rit.id}`)).data;
      if (!R.stops?.length) { toast.error('Deze route heeft geen bezoeken meer'); return; }
      setPlan({
        datum: R.datum, vertrek_uur: R.vertrek_uur, bezoektijd_min: R.bezoektijd_min || 0, roundtrip: R.roundtrip !== false,
        optimaliseer: 'tijd', depot: R.depot || {}, dagen_totaal: 1, totaal_stops: R.stops.length, totaal_afstand_m: R.totaal_afstand_m || 0,
        waarschuwingen: [],
        dagen: [{ dag_nr: R.dag_nr || 1, datum: R.datum, stops: R.stops, terug: R.terug, totaal_afstand_m: R.totaal_afstand_m || 0, rij_duur_s: R.rij_duur_s || 0, totaal_duur_s: R.totaal_duur_s || 0, eind_uur: R.eind_uur || '', handmatig: true }],
      });
      setBewerk({ id: R.id, dag_nr: R.dag_nr || 1, verkoper_naam: R.verkoper_naam || '', datum: R.datum, bezocht: (R.stops || []).filter(s => s.status && s.status !== 'open').length });
      setDatum(R.datum);
      setTitel(R.titel || '');
      setRoundtrip(R.roundtrip !== false);
      setBezoektijd(R.bezoektijd_min || 20);
      setVerkoperId(R.verkoper_id || '');
      setDagIdx(0);
      setKaartView(null);
      setStap(2);
      toast.success('Route geopend — pas de volgorde aan en bewaar de wijzigingen');
    } catch (e) { toast.error('Openen mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const bewerkStop = () => { setBewerk(null); setPlan(null); setStap(3); };

  const opslaan = async () => {
    if (!plan) return;
    setBezig('opslaan');
    try {
      if (bewerk) {
        await API.put(`/verkoopritten/${bewerk.id}`, { planning: plan, dag_nr: plan.dagen[0]?.dag_nr || 1, verkoper_id: verkoperId === '__none__' ? '' : verkoperId, titel, datum });
        toast.success('Bezoekroute bijgewerkt — de link van de verkoper blijft werken');
        setBewerk(null);
        setPlan(null);
        setStap(3);
        await laadRitten();
        return;
      }
      const r = await API.post('/verkoopritten', { planning: plan, verkoper_id: verkoperId === '__none__' ? '' : verkoperId, titel, postcode_van: pcVan, postcode_tot: pcTot });
      toast.success(`${r.data.ritten.length} bezoekroute(s) opgeslagen`);
      setPlan(null);
      setStap(3);
      await laadRitten();
    } catch (e) { toast.error('Opslaan mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };

  const ritPdf = (rit, alle = false) => {
    // Publieke token-URL: opent rechtstreeks in de PDF-viewer. Een blob-download of een popup ná een
    // API-call wordt op gsm/tablet geblokkeerd — vandaar deze synchrone link.
    const p = new URLSearchParams();
    if (alle) p.set('alle_dagen', 'true');
    if (kaartView && bewerk?.id === rit.id) {
      p.set('kaart_lat', kaartView.lat); p.set('kaart_lon', kaartView.lon); p.set('kaart_zoom', kaartView.zoom);
      p.set('kaart_b', kaartView.breedte); p.set('kaart_h', kaartView.hoogte);
    }
    openUrl(`${process.env.REACT_APP_BACKEND_URL}/api/public/verkooprit/${rit.token}/pdf?${p.toString()}`);
  };
  const ritExcel = (rit, alle = false) => {
    openUrl(`${process.env.REACT_APP_BACKEND_URL}/api/public/verkooprit/${rit.token}/excel${alle ? '?alle_dagen=true' : ''}`);
  };
  const planExport = async (formaat, dagNr) => {
    if (!plan) return;
    // Op gsm/tablet moet het venster SYNCHROON open (vóór de await), anders blokkeert de popup-blokker het
    const venster = (formaat === 'pdf' && isMobiel()) ? window.open('', '_blank') : null;
    setBezig(`export-${formaat}-${dagNr || 'alle'}`);
    try {
      const body = { planning: plan, formaat, dag_nr: dagNr || null, verkoper_id: verkoperId === '__none__' ? '' : verkoperId, titel, postcode_van: pcVan, postcode_tot: pcTot, kaart_view: kaartView ? { ...kaartView, dag_nr: dag?.dag_nr || null } : null };
      const wie = veilig((kand?.verkopers || []).find(v => v.id === verkoperId)?.naam);
      const fname = `bezoekroute-${dagNr ? plan.dagen[dagNr - 1]?.datum : `${plan.dagen[0]?.datum}-alle-dagen`}-${wie}.${formaat === 'excel' ? 'xlsx' : 'pdf'}`;
      if (venster || (formaat === 'pdf' && isIOSPlatform())) {
        const r = await API.post('/verkoopritten/export', { ...body, mode: 'token' });
        const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
        if (venster) venster.location.href = url; else openUrl(url);
        return;
      }
      const r = await API.post('/verkoopritten/export', body, { responseType: 'blob' });
      toast.success(`${formaat === 'excel' ? 'Excel' : 'PDF'} gedownload: ${downloadBlob(r, fname)}`);
    } catch (e) {
      if (venster) venster.close();
      let msg = e.message;
      try { if (e.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; else msg = e.response?.data?.detail || msg; } catch { /* laat msg */ }
      toast.error(`${formaat === 'excel' ? 'Excel' : 'PDF'} mislukt: ${msg}`);
    } finally { setBezig(''); }
  };
  const ritWhatsApp = async (rit) => {
    try {
      let tel = rit.verkoper_tel || '';
      if (!tel) { tel = window.prompt('GSM-nummer van de verkoper (bv. 0470 12 34 56):', '') || ''; if (!tel) return; }
      const r = await API.post(`/verkoopritten/${rit.id}/whatsapp-link`, { tel });
      openUrl(r.data.url);
    } catch (e) { toast.error('WhatsApp-link mislukt: ' + (e.response?.data?.detail || e.message)); }
  };
  const ritLink = async (rit) => {
    const url = `${window.location.origin}/verkooprit/${rit.token}`;
    try { await navigator.clipboard.writeText(url); toast.success('Checklist-link gekopieerd — werkt zonder login op elke gsm'); } catch { window.prompt('Checklist-link:', url); }
  };
  const ritVerwijder = async (rit) => {
    if (!window.confirm(`Bezoekroute van ${fmtDatum(rit.datum)} verwijderen? Reeds afgevinkte bezoeken blijven in de historiek.`)) return;
    try { await API.delete(`/verkoopritten/${rit.id}`); toast.success('Verwijderd'); laadRitten(); }
    catch (e) { toast.error('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message)); }
  };

  const dag = plan?.dagen?.[dagIdx];
  const alleSel = kand?.klanten?.length && sel.size === kand.klanten.length;
  const isVerkoper = !!kand?.is_verkoper;

  return (
    <div className="pb-28 lg:pb-8" data-testid="verkoop-route-page">
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={() => navigate('/klantbezoeken')} className="h-10 w-10 inline-flex items-center justify-center border border-gray-200 bg-white" data-testid="vr-terug"><CaretLeft size={18} /></button>
        <div className="min-w-0">
          <h1 className="page-heading mb-0 truncate">Bezoekroute verkopers</h1>
          <div className="text-xs text-gray-500 hidden sm:block">Postcodebereik + opvolging → snelste volgorde → PDF, Excel, WhatsApp en afvink-checklist op de gsm</div>
        </div>
      </div>

      {/* Stappen */}
      <div className="flex gap-1 mb-4 text-xs font-bold">
        {[[1, 'Klanten'], [2, 'Route'], [3, 'Opgeslagen']].map(([n, l]) => (
          <button key={n} type="button" onClick={() => (n === 2 ? plan : true) && setStap(n)} className={`flex-1 h-9 border ${stap === n ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-200'} ${n === 2 && !plan ? 'opacity-40' : ''}`} data-testid={`vr-stap-${n}`}>{n}. {l}</button>
        ))}
      </div>

      {stap === 1 && (
        <>
          <FavoRondes favos={favos} bezig={bezig} datum={datum} kanBewaren={!!favoIds.length} onBewaar={favoBewaar} onLaden={favoLaden} onRijden={favoRijden} onWis={favoWis} />
          {/* Twee manieren om klanten te kiezen */}
          <div className="flex gap-1 mb-3 text-xs font-bold" data-testid="vr-modus">
            <button type="button" onClick={() => wisselModus('postcode')} className={`flex-1 h-11 sm:h-10 border ${modus === 'postcode' ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-200'}`} data-testid="vr-modus-postcode">📍 Op postcodebereik</button>
            <button type="button" onClick={() => wisselModus('zelf')} className={`flex-1 h-11 sm:h-10 border ${modus === 'zelf' ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-200'}`} data-testid="vr-modus-zelf">✋ Klanten zelf kiezen</button>
          </div>

          {modus === 'zelf' ? (
            <div className="bg-white border border-gray-200 p-3 sm:p-4 mb-3" data-testid="vr-zelf-kiezen">
              <label className="form-label">Zoek een klant (naam, bedrijf, stad of postcode) en voeg ze toe</label>
              <div className="flex gap-2">
                <input className="field-input h-11 flex-1" placeholder="bv. Antwerpen, Gent, 1000, Gontex…" value={zoekTerm}
                  onChange={e => setZoekTerm(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); zoekKlanten(); } }}
                  enterKeyHint="search" autoComplete="off" data-testid="vr-zoek-klant" />
                <button type="button" onClick={zoekKlanten} disabled={bezig === 'zoekklant'} className="btn btn-primary h-11 px-4 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="vr-zoek-klant-btn">
                  <MagnifyingGlass size={16} weight="bold" /> {bezig === 'zoekklant' ? '…' : 'Zoek'}
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">Zo maakt u een gemengde route: bv. 2 klanten uit Antwerpen, 2 uit Gent en 2 uit Brussel. Zoek per stad en tik op “Toevoegen”.</p>
              {zoekRes.length > 0 && (
                <div className="mt-3 border border-gray-200 max-h-[40vh] overflow-y-auto" data-testid="vr-zoek-resultaten">
                  {zoekRes.map(k => {
                    const al = (kand?.klanten || []).some(x => x.klant_id === k.klant_id);
                    return (
                      <div key={k.klant_id} className="flex items-center gap-2 p-2.5 border-b border-gray-100" data-testid={`vr-zoekres-${k.klant_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{k.klant_naam}</div>
                          <div className="text-xs text-gray-600 truncate">{k.adres?.tekst}</div>
                          <div className="flex flex-wrap gap-x-3 text-[11px] text-gray-500 mt-0.5">
                            <PrioPill p={k.prioriteit} />
                            <span>Laatste bezoek: <b>{fmtDD(k.laatste_bezoek)}</b></span>
                            {k.openstaand > 0 && <span className="text-red-600 font-bold">{fmtEur(k.openstaand)}</span>}
                          </div>
                        </div>
                        <button type="button" disabled={al} onClick={() => voegToe(k)} className={`h-10 px-3 text-xs font-bold border shrink-0 ${al ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-[#002FA7] text-[#002FA7] bg-white'}`} data-testid={`vr-voegtoe-${k.klant_id}`}>
                          {al ? '✓ Gekozen' : '+ Toevoegen'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
          <div className="bg-white border border-gray-200 p-3 sm:p-4 mb-3" data-testid="vr-filters">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><label className="form-label">Postcode van</label><input className="field-input h-11" inputMode="numeric" placeholder="1000" value={pcVan} onChange={e => setPcVan(e.target.value)} data-testid="vr-pc-van" /></div>
              <div><label className="form-label">Postcode tot</label><input className="field-input h-11" inputMode="numeric" placeholder="2000" value={pcTot} onChange={e => setPcTot(e.target.value)} data-testid="vr-pc-tot" /></div>
              {!isVerkoper && (
                <div className="col-span-2 sm:col-span-1"><label className="form-label">Verkoper</label>
                  <select className="field-input h-11" value={verkoperId} onChange={e => setVerkoperId(e.target.value)} data-testid="vr-verkoper">
                    <option value="">Alle klanten</option>
                    <option value="__none__">Zonder vaste verkoper</option>
                    {(kand?.verkopers || []).map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
                  </select>
                </div>
              )}
              <div className="col-span-2 sm:col-span-1 flex items-end">
                <button type="button" onClick={() => zoek()} disabled={bezig === 'zoek'} className="btn btn-primary h-11 w-full inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="vr-zoek"><MagnifyingGlass size={16} weight="bold" /> {bezig === 'zoek' ? 'Zoeken…' : 'Klanten zoeken'}</button>
              </div>
            </div>
            <label className="inline-flex items-center gap-2 mt-3 text-sm cursor-pointer">
              <input type="checkbox" className="w-5 h-5" checked={alleenOpvolging} onChange={e => setAlleenOpvolging(e.target.checked)} data-testid="vr-alleen-opvolging" />
              Alleen klanten die opvolging nodig hebben (lang niet bezocht / geen aankoop)
            </label>
          </div>
          )}

          {kand && (
            <div className="bg-white border border-gray-200" data-testid="vr-kandidaten">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 text-xs">
                <label className="inline-flex items-center gap-2 font-semibold cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={!!alleSel} onChange={() => setSel(alleSel ? new Set() : new Set(kand.klanten.map(k => k.klant_id)))} data-testid="vr-select-all" /> {sel.size} van {kand.klanten.length} {modus === 'zelf' ? 'gekozen klanten' : 'geselecteerd'}</label>
                <span className="text-gray-500">{zonderGeo.length ? `${zonderGeo.length} nog zonder coördinaten` : '✓ alle adressen gekend'}</span>
              </div>
              {geoFouten.length > 0 && (
                <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800" data-testid="vr-geo-fouten">
                  <Warning size={14} className="inline mr-1" /> Niet gevonden (controleer het adres op de klantenfiche): {geoFouten.map(f => f.klant_naam).join(', ')}
                </div>
              )}
              <div className="max-h-[52vh] overflow-y-auto">
                {kand.klanten.map(k => <KandidaatRij key={k.klant_id} k={k} checked={sel.has(k.klant_id)} onToggle={() => setSel(p => { const n = new Set(p); n.has(k.klant_id) ? n.delete(k.klant_id) : n.add(k.klant_id); return n; })} onVerwijder={modus === 'zelf' ? () => haalWeg(k.klant_id) : null} />)}
                {!kand.klanten.length && <div className="p-6 text-center text-sm text-gray-400">{modus === 'zelf' ? 'Nog geen klanten gekozen — zoek hierboven en tik op “Toevoegen”' : 'Geen klanten in dit bereik'}</div>}
              </div>
            </div>
          )}

          {kand && (
            <div className="bg-white border border-gray-200 p-3 sm:p-4 mt-3" data-testid="vr-instellingen">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><label className="form-label">Startdatum</label><input type="date" className="field-input h-11" value={datum} onChange={e => setDatum(e.target.value)} data-testid="vr-datum" /></div>
                <div><label className="form-label">Max. bezoeken / dag</label><input type="number" min="1" max="40" className="field-input h-11" value={maxPerDag} onChange={e => setMaxPerDag(e.target.value)} data-testid="vr-max-per-dag" /></div>
                <div><label className="form-label">Bezoektijd (min)</label><input type="number" min="0" className="field-input h-11" value={bezoektijd} onChange={e => setBezoektijd(e.target.value)} data-testid="vr-bezoektijd" /></div>
                <div className="flex items-end"><label className="inline-flex items-center gap-2 h-11 text-sm cursor-pointer"><input type="checkbox" className="w-5 h-5" checked={roundtrip} onChange={e => setRoundtrip(e.target.checked)} data-testid="vr-roundtrip" /> Terug naar bedrijf</label></div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-xs font-semibold text-gray-600">Volgorde optimaliseren op:</span>
                <div className="inline-flex border border-gray-300 text-xs font-bold" data-testid="vr-optimaliseer">
                  <button type="button" onClick={() => setOptimaliseer('tijd')} className={`h-9 px-3 ${optimaliseer === 'tijd' ? 'bg-[#002FA7] text-white' : 'bg-white text-gray-600'}`} data-testid="vr-optimaliseer-tijd">⏱ Kortste tijd</button>
                  <button type="button" onClick={() => setOptimaliseer('afstand')} className={`h-9 px-3 border-l border-gray-300 ${optimaliseer === 'afstand' ? 'bg-[#002FA7] text-white' : 'bg-white text-gray-600'}`} data-testid="vr-optimaliseer-afstand">📏 Minste km</button>
                </div>
                <button type="button" disabled={geoBezig || !geselecteerd.length} onClick={() => geocodeer(true)} className="h-9 px-3 text-xs font-semibold border border-gray-300 bg-white inline-flex items-center gap-1.5 disabled:opacity-50 sm:ml-auto" title="Alle geselecteerde adressen opnieuw opzoeken (bv. na het verbeteren van een adres)" data-testid="vr-hergeocodeer">
                  <ArrowsClockwise size={14} weight="bold" className={geoBezig ? 'animate-spin' : ''} /> Adressen opnieuw opzoeken
                </button>
              </div>
              <div className="text-[11px] text-gray-500 mt-2"><MapPin size={12} className="inline" /> Vertrek: {kand.depot?.tekst || '— bedrijfsadres ontbreekt in Instellingen —'} · {kand.vertrek_uur}. Meer klanten dan het maximum? Dan verdeelt de app ze automatisch over opeenvolgende werkdagen.</div>
            </div>
          )}
        </>
      )}

      {stap === 2 && plan && (
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-3" data-testid="vr-plan">
          <div>
            <div className="flex gap-1.5 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
              {plan.dagen.map((d, i) => (
                <button key={d.dag_nr} type="button" onClick={() => setDagIdx(i)} className={`shrink-0 px-3 h-12 border text-left ${i === dagIdx ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-gray-200'}`} data-testid={`vr-dag-${d.dag_nr}`}>
                  <div className="text-xs font-bold">Dag {d.dag_nr} · {fmtDatum(d.datum)}</div>
                  <div className={`text-[10px] ${i === dagIdx ? 'opacity-80' : 'text-gray-500'}`}>{d.stops.length} bezoeken · {(d.totaal_afstand_m / 1000).toFixed(0)} km · tot {d.eind_uur}</div>
                </button>
              ))}
            </div>
            {dag && (
              <div className="bg-white border border-gray-200">
                <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 text-xs flex flex-wrap gap-x-4 gap-y-1">
                  <span><b>{dag.stops.length}</b> bezoeken</span><span><b>{(dag.totaal_afstand_m / 1000).toFixed(1)}</b> km</span><span>rijden <b>{fmtDuur(dag.rij_duur_s)}</b></span><span>totaal <b>{fmtDuur(dag.totaal_duur_s)}</b></span><span>{plan.vertrek_uur} → {dag.eind_uur}</span>
                  {dag.handmatig ? <span className="text-amber-700 font-semibold" data-testid="vr-dag-handmatig">✎ eigen volgorde</span> : <span className="text-emerald-700 font-semibold">✓ optimale volgorde ({plan.optimaliseer === 'afstand' ? 'minste km' : 'kortste tijd'})</span>}
                </div>
                {plan.waarschuwingen?.length > 0 && (
                  <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800" data-testid="vr-plan-waarschuwingen">
                    <Warning size={13} className="inline mr-1" weight="fill" /><b>{plan.waarschuwingen.length} adres(sen) niet exact gevonden</b> — de route rond die stops kan afwijken. Controleer: {plan.waarschuwingen.map(w => w.klant_naam).join(', ')}.
                  </div>
                )}
                <div className={`max-h-[48vh] lg:max-h-[60vh] overflow-y-auto ${bezig === 'herbereken' ? 'opacity-50 pointer-events-none' : ''}`}>
                  {dag.stops.map((s, i) => <StopRij key={s.klant_id} s={s} kleur={kleur} idx={i} totaal={dag.stops.length} onOmhoog={() => verschuif(i, i - 1)} onOmlaag={() => verschuif(i, i + 1)} onVerwijder={() => verwijderStop(i)} />)}
                  {dag.terug && <div className="text-center text-xs text-gray-500 py-2">↩ Terug op het bedrijf ± <b>{dag.terug.eta}</b></div>}
                </div>
                <div className="px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100">Volgorde berekend op de echte rijtijden/afstanden tussen alle adressen. Kent u het beter? Verschuif een stop met ▲▼ — de tijden worden meteen herberekend.</div>
                <div className={`p-2 border-t border-gray-200 space-y-2 ${bezig === 'herbereken' ? 'opacity-60 pointer-events-none' : ''}`} data-testid="vr-dag-aanpassen">
                  <KlantBijzetten onZoek={bijzetZoek} onBijzetten={bijzetKlant} kleur={kleur}
                    hint={`De klant komt automatisch op de slimste plek${bewerk ? ' in deze bewaarde route' : ''} — uren en km worden herberekend. Vergeet niet te bewaren.`} />
                  <button type="button" disabled={!!bezig || dag.stops.length < 3} onClick={optimaliseerDag}
                    className="w-full h-10 border border-gray-300 bg-white text-xs font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-40" data-testid="vr-optimaliseer-opnieuw">
                    <ArrowsClockwise size={14} weight="bold" className={bezig === 'herbereken' ? 'animate-spin' : ''} /> Volgorde opnieuw optimaliseren ({optimaliseer === 'afstand' ? 'minste km' : 'kortste tijd'})
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 p-2 border-t border-gray-200 bg-gray-50 text-xs font-semibold" data-testid="vr-plan-export">
                  <button type="button" disabled={!!bezig} onClick={() => planExport('pdf', dag.dag_nr)} className="h-9 px-3 border border-gray-300 bg-white inline-flex items-center gap-1 disabled:opacity-50" data-testid="vr-plan-pdf-dag"><FilePdf size={14} className="text-red-600" /> PDF dag {dag.dag_nr}</button>
                  <button type="button" disabled={!!bezig} onClick={() => planExport('excel', dag.dag_nr)} className="h-9 px-3 border border-gray-300 bg-white inline-flex items-center gap-1 disabled:opacity-50" data-testid="vr-plan-excel-dag"><FileXls size={14} className="text-emerald-700" /> Excel dag {dag.dag_nr}</button>
                  {plan.dagen_totaal > 1 && (
                    <>
                      <button type="button" disabled={!!bezig} onClick={() => planExport('pdf')} className="h-9 px-3 border border-gray-300 bg-white inline-flex items-center gap-1 disabled:opacity-50" data-testid="vr-plan-pdf-alle"><FilePdf size={14} className="text-red-600" /> PDF alle {plan.dagen_totaal} dagen</button>
                      <button type="button" disabled={!!bezig} onClick={() => planExport('excel')} className="h-9 px-3 border border-gray-300 bg-white inline-flex items-center gap-1 disabled:opacity-50" data-testid="vr-plan-excel-alle"><FileXls size={14} className="text-emerald-700" /> Excel alle dagen</button>
                    </>
                  )}
                  <span className="text-[10px] text-gray-400 self-center ml-auto">{bezig.startsWith('export') ? 'Bezig…' : 'De kaart wordt afgedrukt zoals u ze hiernaast ziet — zoom met + / −'}</span>
                </div>
              </div>
            )}
            <div className="bg-white border border-gray-200 p-3 mt-3" data-testid="vr-opslaan-blok">
              {bewerk && (
                <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-center gap-2 flex-wrap" data-testid="vr-bewerk-banner">
                  <PencilSimple size={14} weight="bold" />
                  <span><b>Bewaarde route aanpassen</b> — {fmtDatum(bewerk.datum)}{bewerk.verkoper_naam ? ` · ${bewerk.verkoper_naam}` : ''}{bewerk.bezocht ? ` · ${bewerk.bezocht} bezoek(en) al afgevinkt blijven bewaard` : ''}</span>
                  <button type="button" onClick={bewerkStop} className="ml-auto underline font-semibold" data-testid="vr-bewerk-annuleer">Annuleren</button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {!isVerkoper && (
                  <div><label className="form-label">Verkoper voor deze route</label>
                    <select className="field-input h-11" value={verkoperId === '__none__' ? '' : verkoperId} onChange={e => setVerkoperId(e.target.value)} data-testid="vr-opslaan-verkoper">
                      <option value="">— Geen verkoper (zelf rijden) —</option>
                      {(kand?.verkopers || []).map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
                      {!kand?.verkopers?.some(v => v.id === verkoperId) && verkoperId && bewerk && <option value={verkoperId}>{bewerk.verkoper_naam || 'Huidige verkoper'}</option>}
                    </select>
                  </div>
                )}
                {bewerk && (
                  <div><label className="form-label">Datum van de route</label>
                    <input type="date" className="field-input h-11" value={datum} onChange={e => setDatum(e.target.value)} data-testid="vr-bewerk-datum" />
                  </div>
                )}
                <div><label className="form-label">Titel (optioneel)</label><input className="field-input h-11" placeholder={pcVan || pcTot ? `Postcode ${pcVan || '…'} – ${pcTot || '…'}` : 'bv. Regio Antwerpen'} value={titel} onChange={e => setTitel(e.target.value)} data-testid="vr-titel" /></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                {bewerk
                  ? <button type="button" onClick={bewerkStop} className="btn btn-secondary h-11 inline-flex items-center justify-center gap-1.5" data-testid="vr-bewerk-terug"><ArrowCounterClockwise size={16} /> Annuleren</button>
                  : <button type="button" onClick={() => setStap(1)} className="btn btn-secondary h-11 inline-flex items-center justify-center gap-1.5"><ArrowCounterClockwise size={16} /> Selectie aanpassen</button>}
                <button type="button" onClick={opslaan} disabled={bezig === 'opslaan'} className="btn btn-primary h-11 flex-1 inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="vr-opslaan"><CheckCircle size={18} weight="fill" /> {bezig === 'opslaan' ? 'Opslaan…' : bewerk ? `Wijzigingen opslaan (${plan.dagen[0]?.stops?.length || 0} bezoeken)` : `Opslaan — ${plan.dagen_totaal} dag(en), ${plan.totaal_stops} bezoeken`}</button>
              </div>
              <button type="button" onClick={favoBewaar} disabled={bezig === 'favo-bewaar'} className="w-full h-10 mt-2 border border-amber-400 text-amber-700 bg-amber-50 text-xs font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-40" data-testid="vr-favo-bewaar-plan">
                <Star size={14} weight="fill" /> {bezig === 'favo-bewaar' ? 'Bewaren…' : 'Deze klantenselectie bewaren als favoriete ronde'}
              </button>
            </div>
          </div>
          <div className="order-first lg:order-none">
            <RouteKaart depot={plan.depot} stops={(dag?.stops || []).map(s => ({ ...s, doc_id: s.klant_id, documentnr: s.klantnr }))} geometry={dag?.geometry} kleur={kleur} hoogte={window.innerWidth < 1024 ? 280 : 620} onView={setKaartView} />
          </div>
        </div>
      )}

      {stap === 3 && (
        <div data-testid="vr-ritten">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <h2 className="text-base font-bold">Geplande bezoekroutes</h2>
            <div className="flex items-center gap-2">
              <select value={sorteer} onChange={e => setSorteer(e.target.value)} className="field-input h-10 text-xs" title="Volgorde van de lijst" data-testid="vr-sorteer">
                <option value="datum">Sorteer: rijdatum</option>
                <option value="nieuw">Sorteer: laatst aangemaakt</option>
              </select>
              <button type="button" onClick={() => { setKand(null); setPlan(null); setStap(1); }} className="btn btn-primary h-10 inline-flex items-center gap-1.5" data-testid="vr-nieuw"><Compass size={16} weight="bold" /> Nieuwe route</button>
            </div>
          </div>
          {!ritten.length && <div className="bg-white border border-gray-200 p-8 text-center text-sm text-gray-400">Nog geen bezoekroutes. Zoek klanten op postcode en bereken een route.</div>}
          <div className="grid gap-2 md:grid-cols-2">
            {rittenGesorteerd.map(r => <RitKaart key={r.id} rit={r} nieuwste={r.id === nieuwsteId} onPdf={() => ritPdf(r)} onExcel={() => ritExcel(r)} onPdfAlle={() => ritPdf(r, true)} onExcelAlle={() => ritExcel(r, true)} onWhatsApp={() => ritWhatsApp(r)} onLink={() => ritLink(r)} onOpen={() => openUrl(`/verkooprit/${r.token}`)} onVerwijder={() => ritVerwijder(r)} onBewerk={() => bewerkStart(r)} />)}
          </div>
        </div>
      )}

      {/* Vaste actiebalk onderaan (gsm) */}
      {stap === 1 && kand && (
        <div className="bf-bottombar fixed left-0 md:left-56 right-0 bottom-0 z-30 bg-white border-t-2 border-[#002FA7] p-3 lg:static lg:border lg:border-gray-200 lg:mt-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }} data-testid="vr-actiebalk">
          <button type="button" onClick={bereken} disabled={!sel.size || geoBezig || bezig === 'plan'} className="btn btn-primary h-12 w-full inline-flex items-center justify-center gap-2 text-base disabled:opacity-50" data-testid="vr-bereken">
            <Path size={20} weight="bold" /> {geoBezig ? 'Adressen op de kaart zetten…' : bezig === 'plan' ? 'Route berekenen…' : `Route berekenen (${sel.size} klanten)`}
          </button>
        </div>
      )}
    </div>
  );
}
